"use client";
import * as S from "./styles.ts";
import {
  H2,
  H3,
  IconBulletPoint,
  InfoBlock,
  P,
  UniversalLink,
} from "@template/components";
import { WebinarForm } from "./Webinar";
import { useTypedSelector } from "@/redux/helpers/hooks.ts";
import { SupportForm } from "./Support";
import { ResellerForm } from "./Reseller";
import ArchStandaloneSectionComponent from "@template/blocks/ArchStandaloneSection/component";
import useBreakpoint from "@/lib/helpers/hooks/useBreakpoint.ts";
import { IInfoBlockProps } from "@template/components/InfoBlock";
import { ReviewsSummary } from "@template/blocks/ReviewsSummary";
import { ColorKey } from "@/template/theme/index.ts";

/**
 * Represents the base properties found in every node.
 */
interface BaseNode {
  text?: string;
  children?: ContentNode[];
}

/**
 * Represents a standard text node without any layout types.
 */
interface TextNode extends BaseNode {
  text: string;
}

/**
 * Represents a container node with a specific layout type.
 */
interface ElementNode extends BaseNode {
  type: "paragraph" | "link";
  url?: string; // Optional because only 'link' types use it
  children: ContentNode[];
}

/**
 * A union type that allows nodes to be either text or elements.
 */
type ContentNode = TextNode | ElementNode;

/**
 * The root structure as seen in your Storybook description.
 */
interface Description {
  type: "paragraph";
  children: ContentNode[];
}

interface IContactSectionProps {
  title?: string;
  description?: string | Description;
  heading?: string;
  subHeading?: string;
  altBackground?: boolean;
  descriptionCtaText?: { label: string; href?: string }[];
  infoBlockDescription?: {
    heading: string;
    subHeading: string;
    iconName: string;
    color: ColorKey;
    cta: {
      text: string;
      link: string;
      withIcon?: boolean;
      target?: "_blank" | "_self";
      title?: string;
    };
    ctaUS?: {
      text: string;
      link: string;
      withIcon?: boolean;
      target?: "_blank" | "_self";
      title?: string;
    };
  }[];
  infoBullets?: {
    description: string;
    bullets: {
      text: string;
    }[];
  };
  infoBlocks?: boolean;
  context?: "supportTicket" | "webinar" | "resellerEnquiry";
  reviews?: boolean;
  enquiryType?: {
    label: string;
    key: string;
  }[];
  fullColumn?: boolean;
  contentFieldMap?: boolean;
  accordionHeader?: string;
  accordionBulletsPoints?: {
    accordionContent?: string;
  }[];
}

const ContactSectionComponent = ({
  title,
  description,
  accordionHeader,
  accordionBulletsPoints,
  infoBlocks,
  altBackground = false,
  heading,
  subHeading,
  context = "supportTicket",
  reviews,
  infoBullets,
  enquiryType,
  infoBlockDescription,
  fullColumn,
  contentFieldMap,
  descriptionCtaText,
}: IContactSectionProps) => {
  const { check: isLg } = useBreakpoint("lg");
  const currency = useTypedSelector(
    (state) => state.store.site_version.currency
  );
  const isGBP = currency === "GBP";

  const yayInfoBlocks: IInfoBlockProps[] = [
    {
      heading: "Email",
      subHeading: "Our friendly team is here to help.",
      iconName: "Mail",
      color: "primary",
      cta: {
        text: "help@yay.com",
        link: "mailto: help@yay.com",
        withIcon: false,
      },
    },
    {
      heading: "Call us",
      subHeading: isGBP
        ? "Mon – Friday, 7am to 10pm, Network Ops run 24/7"
        : "Mon – Friday, 5am to 5pm, Network Ops run 24/7",
      iconName: "PhoneCall",
      color: "primary",
      cta: {
        text: isGBP ? "0330 122 6000" : "+1 (888) 929 8747", // Update this with intl number isGBP ternary if applicable
        link: isGBP ? "tel:03301226000" : "tel:+18889298747",
        withIcon: false,
      },
    },
    {
      heading: "Visit us",
      subHeading: "Yay.com Headquarters",
      iconName: "MapPin",
      color: "primary",
      cta: {
        text: isGBP
          ? "Unit 4, Riverside Business Park Walnut Tree Close Guildford, GU1 4UG"
          : "26 Broadway, 8th Floor, New York, NY 10004, USA",
        link: "https://www.google.com/maps/dir//Unit+4,+Riverside+Business+Park,+Walnut+Tree+Cl,+Guildford+GU1+4UG/@51.241815,-0.6623577,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x4875d0915c7d84ab:0xcdb88a3a6e150faa!2m2!1d-0.5799575!2d51.2418439?entry=ttu",
        withIcon: false,
        target: "_blank",
      },
    },
    {
      heading: "99.9% uptime",
      subHeading: "We’re proud of our 99.9% uptime",
      iconName: "Activity",
      color: "primary",
      cta: {
        text: "System Status",
        link: "https://status.yay.com/?_gl=1*1nkl3gm*_ga*NDI3ODY2MjgzLjE3MTQ0NjkxMzM.*_ga_3QFM6NW4D1*MTcxNTY5MDA3NC40MS4xLjE3MTU2OTE2NjEuNjAuMC4xNDAzNzAwODI2",
        withIcon: false,
        target: "_blank",
      },
    },
  ];

  const hasCopy = (title || description || reviews) && !contentFieldMap;
  const hasInfoBullets = !!infoBullets;
  const altLayout = altBackground && !hasCopy && (infoBlocks || hasInfoBullets);

  return (
    <S.ContactSectionWrapper
      $altBackground={altBackground}
      id="contact-us"
    >
      <S.ContactSectionContainer $fullWidth={fullColumn}>
        {hasCopy && (
          <S.ContactSectionContent
            $fullWidth={fullColumn}
            $altLayout={altBackground}
          >
            <S.ContactSectionHeader>Contact Us</S.ContactSectionHeader>
            {title && <H3>{title}</H3>}
            {description && <P>{description}</P>}
            {descriptionCtaText && (
              <P>
                {descriptionCtaText?.map((segment, index) =>
                  segment.href ? (
                    <UniversalLink
                      key={index}
                      href={segment.href}
                    >
                      {segment.label}
                    </UniversalLink>
                  ) : (
                    segment.label
                  )
                )}
              </P>
            )}
            {accordionBulletsPoints?.length > 0 && (
              <S.BulletPointContainer>
                {accordionHeader && <P>{accordionHeader}</P>}
                {accordionBulletsPoints?.map((bullet, index) => (
                  <IconBulletPoint
                    key={index}
                    item={{
                      label: bullet.accordionContent,
                    }}
                    withIcon={true}
                  />
                ))}
              </S.BulletPointContainer>
            )}

            {reviews && <ReviewsSummary />}
          </S.ContactSectionContent>
        )}

        {(altLayout || contentFieldMap) && (
          <>
            {altBackground && (
              <ArchStandaloneSectionComponent
                color="white"
                background="dark"
                style={{
                  position: "absolute",
                  left: 0,
                  top: "var(--split-point)",
                  transform: "translateY(-50%)",
                }}
              />
            )}
            <S.LeftColumn>
              <S.HeadingContainer $altBackground={altBackground}>
                <H2>{heading}</H2>
                <P>{subHeading}</P>
              </S.HeadingContainer>
              {accordionBulletsPoints?.length > 0 && (
                <S.BulletPointContainer>
                  <P>{accordionHeader}</P>
                  {accordionBulletsPoints?.map((bullet, index) => (
                    <IconBulletPoint
                      key={index}
                      item={{
                        label: bullet.accordionContent,
                      }}
                      withIcon={true}
                    />
                  ))}
                </S.BulletPointContainer>
              )}

              {infoBlocks ? (
                <S.InfoBlocksContainer>
                  {yayInfoBlocks.map((info, index) => (
                    <InfoBlock
                      key={index}
                      heading={info.heading}
                      subHeading={info.subHeading}
                      iconName={info.iconName}
                      color={info.color}
                      cta={info.cta}
                      small={true}
                    />
                  ))}
                </S.InfoBlocksContainer>
              ) : (
                <S.InfoBlocksContainer>
                  {infoBlockDescription?.map((info, index) => (
                    <InfoBlock
                      key={index}
                      heading={info.heading}
                      subHeading={info.subHeading}
                      iconName={info.iconName}
                      color={info.color}
                      cta={!isGBP && info.ctaUS ? info.ctaUS : info.cta}
                      small={true}
                    />
                  ))}
                </S.InfoBlocksContainer>
              )}
              {hasInfoBullets && (
                <S.InfoBulletsContainer>
                  <P>{infoBullets?.description}</P>
                  <S.InfoBullets>
                    {infoBullets?.bullets?.map((bullet, index) => (
                      <IconBulletPoint
                        key={index}
                        item={{
                          label: bullet.text,
                        }}
                      />
                    ))}
                  </S.InfoBullets>
                </S.InfoBulletsContainer>
              )}
            </S.LeftColumn>
          </>
        )}

        <S.ContactSectionForm $fullWidth={fullColumn}>
          {context === "webinar" && <WebinarForm />}
          {context === "supportTicket" && (
            <SupportForm enquiryType={enquiryType} />
          )}
          {context === "resellerEnquiry" && <ResellerForm />}
        </S.ContactSectionForm>
      </S.ContactSectionContainer>
    </S.ContactSectionWrapper>
  );
};

export default ContactSectionComponent;

export const options = [
  {
    description: "",
    displayName: "ContactSectionComponent",
    methods: [],
    props: {
      title: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      description: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      descriptionCtaText: {
        required: false,
        flowType: {
          name: "Array",
          elements: [
            {
              name: "signature",
              type: "object",
              raw: "{ label: string; href?: string }",
              signature: {
                properties: [
                  {
                    key: "label",
                    value: {
                      name: "string",
                      required: true,
                    },
                  },
                  {
                    key: "href",
                    value: {
                      name: "string",
                      required: false,
                    },
                  },
                ],
              },
            },
          ],
          raw: "{ label: string; href?: string }[]",
        },
        description: "",
      },
      heading: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      subHeading: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      altBackground: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
        defaultValue: { value: "false", computed: false },
      },
      infoBullets: {
        required: false,
        flowType: {
          name: "signature",
          type: "object",
          raw: "{\n  description: string;\n  bullets: string[];\n}",
          signature: {
            properties: [
              {
                key: "description",
                value: {
                  name: "string",
                  required: true,
                },
              },
              {
                key: "bullets",
                value: {
                  name: "Array",
                  elements: [{ name: "string" }],
                  raw: "string[]",
                  required: true,
                },
              },
            ],
          },
        },
        description: "",
      },
      infoBlocks: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
      },
      context: {
        required: false,
        flowType: {
          name: "union",
          raw: '"supportTicket" | "webinar" | "resellerEnquiry"',
          elements: [
            {
              name: "literal",
              value: '"supportTicket"',
            },
            { name: "literal", value: '"webinar"' },
            {
              name: "literal",
              value: '"resellerEnquiry"',
            },
          ],
        },
        description: "",
        defaultValue: {
          value: '"supportTicket"',
          computed: false,
        },
      },
      reviews: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
      },
    },
  },
];
